// @ts-nocheck — composed component; types added when touched again later
import React from "react";
// Phase 3f — chart legend swatch routes through the v2 primitive.
import V2Swatch from "../ui/Swatch";

/* composed/AEChart.tsx — per-AE comparison chart (Phase 4 sub-step 10a).
   Relocated from src/legacy/ae-chart.tsx (last file under src/legacy/
   to graduate besides the primitives.tsx compat shell).

   Renders one column per AE, with sub-columns per month showing
   real-vs-target. Pure render, no state mutations.

   Same window-globals as before: window.MONTH_KEYS, MONTH_SHORT,
   __FMT_MONEY_MRR_PURE (from lib/model). */

// AE comparison chart — grouped vertical columns per AE per month
// Each AE-month is a bar showing REAL contribution; the bar height encodes
// % of target hit (with full target shown as a faint outline, % label inside).

// Palette read from the design-system tokens via window.TOKENS.chart (B4/A3).
// 8 categorical hues — chart-1..chart-8 are owned by tokens.css; chart-6/7/8
// are reserved/teal/magenta so future channels can extend without recoloring.
const AE_PALETTE = (() => {
  const c = (window.TOKENS && window.TOKENS.chart) || {};
  return [
    c[1], c[4], c[2], c[3], c[5], c[7], c[6], c[8],
  ].filter(Boolean);
})();

function AEComparisonChart({ rows, aes, lang, valueMode, todayMonthKey, aeMonthlyTarget }) {
  const w = 1440, h = 380;
  const pad = { l: 100, r: 36, t: 32, b: 56 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;

  // This chart has its OWN MRR/ARR toggle. We bypass any global rebinding of window.fmtMoney
  // (which gets ×N from the global toggle) and use the original MRR formatter, scaling values
  // by 12 ourselves when in ARR mode. Saved by app.jsx as __fmtMoneyMrr at module load.
  const mul = valueMode === "arr" ? 12 : 1;
  const fmtMrr = (typeof window !== "undefined" && window.__fmtMoneyMrr) || window.fmtMoney || ((n) => "€" + Math.round(n));

  if (!aes || aes.length === 0) {
    return (
      <div className="ae-chart-empty">
        {lang === "it" ? "Aggiungi un AE per visualizzare il confronto." : "Add an AE to see the comparison."}
      </div>
    );
  }

  // Build per-AE per-month data: target + real (scaled by ARR multiplier)
  const series = aes.map((ae, idx) => ({
    ae,
    color: AE_PALETTE[idx % AE_PALETTE.length],
    points: rows.map((r) => {
      const aeRow = (r.aes || []).find(a => a.id === ae.id);
      return {
        monthKey: r.key,
        real: aeRow && typeof aeRow.real === "number" ? aeRow.real * mul : null,
        target: aeRow ? (aeRow.target || 0) * mul : 0,
      };
    }),
  }));

  // Compute max across all AE values
  const allValues = [];
  series.forEach(s => s.points.forEach(p => {
    if (p.real != null) allValues.push(p.real);
    if (p.target > 0) allValues.push(p.target);
  }));
  const dataMax = Math.max(1000, ...allValues);
  const niceCeil = (v) => {
    if (v <= 0) return 1000;
    const exp = Math.floor(Math.log10(v));
    const pow = Math.pow(10, exp);
    const norm = v / pow;
    const stops = [1, 1.2, 1.25, 1.5, 1.6, 1.8, 2, 2.2, 2.5, 2.8, 3, 3.2, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 10];
    let nice = 10;
    for (const s of stops) { if (norm <= s) { nice = s; break; } }
    return nice * pow;
  };
  const niceMax = niceCeil(dataMax * 1.05);

  const xStep = innerW / Math.max(1, rows.length);
  const xMonthCenter = (i) => pad.l + xStep * (i + 0.5);
  const yScale = (v) => pad.t + innerH * (1 - Math.max(0, v) / niceMax);

  const todayIdx = rows.findIndex(r => r.key === todayMonthKey);

  const ticks = [];
  for (let i = 0; i <= 4; i++) ticks.push((niceMax * i) / 4);
  const fmtTick = (v) => fmtMrr(v);

  // Per-AE bar within month group
  const groupPad = 0.18;       // % of step left/right empty
  const aeCount = series.length;
  const groupW = xStep * (1 - groupPad * 2);
  const subW = aeCount > 0 ? groupW / aeCount : groupW;
  const barW = Math.max(4, subW * 0.78);

  const totals = series.map(s => s.points.reduce((sum, p) => sum + (p.real || 0), 0));

  return (
    <div className="ae-chart-wrap">
    <svg viewBox={`0 0 ${w} ${h}`} className="chart" preserveAspectRatio="xMidYMid meet">
      <rect x={pad.l} y={pad.t} width={innerW} height={innerH} className="chart__bg" />
      {/* y-grid */}
      {ticks.map((tv, i) => (
        <g key={i}>
          <line x1={pad.l} x2={pad.l + innerW} y1={yScale(tv)} y2={yScale(tv)}
            stroke={tv === 0 ? "var(--ink-2)" : "var(--line)"}
            strokeWidth={tv === 0 ? 1.4 : 1}
            strokeDasharray={tv === 0 ? "0" : "2 4"}
            opacity={tv === 0 ? 0.55 : 1}
          />
          <text x={pad.l - 12} y={yScale(tv) + 4} textAnchor="end" className="chart__tick">{fmtTick(tv)}</text>
        </g>
      ))}

      {/* Target reference line — horizontal dotted line at the global per-AE monthly target */}
      {aeMonthlyTarget > 0 && (() => {
        const tv = aeMonthlyTarget * mul;
        if (tv > niceMax) return null; // off-chart, skip
        const y = yScale(tv);
        return (
          <g>
            <line x1={pad.l} x2={pad.l + innerW} y1={y} y2={y}
              stroke="var(--ae-500)" strokeWidth="1.25" strokeDasharray="4 4" opacity="0.85" />
            {/* Y-axis label for the target value, even if it doesn't align with a tick */}
            <text x={pad.l - 12} y={y + 4} textAnchor="end" className="chart__tick"
              style={{ fill: "var(--ae-500)", fontWeight: 400 }}>
              {fmtMrr(tv)}
            </text>
          </g>
        );
      })()}

      {/* Today marker */}
      {todayIdx >= 0 && (
        <g>
          <line x1={xMonthCenter(todayIdx)} x2={xMonthCenter(todayIdx)} y1={pad.t} y2={pad.t + innerH}
            stroke="var(--lilac-700)" strokeWidth="1.5" strokeDasharray="3 4" opacity="0.55" />
          <text x={xMonthCenter(todayIdx)} y={pad.t - 8} textAnchor="middle" className="chart__today-tag">
            {lang === "it" ? "Oggi" : "Today"}
          </text>
        </g>
      )}

      {/* Bars: one column per AE per month */}
      {rows.map((r, monthIdx) => {
        const groupX = xMonthCenter(monthIdx) - groupW / 2;
        return (
          <g key={r.key}>
            {series.map((s, aeIdx) => {
              const p = s.points[monthIdx];
              const cx = groupX + subW * (aeIdx + 0.5);
              const x = cx - barW / 2;
              const targetH = p.target > 0 ? (p.target / niceMax) * innerH : 0;
              const realH = (p.real != null && p.real > 0) ? (p.real / niceMax) * innerH : 0;
              const yReal = pad.t + innerH - realH;
              const yTarget = pad.t + innerH - targetH;
              const pct = (p.real != null && p.target > 0) ? (p.real / p.target) * 100 : null;
              const tone = pct == null ? "neutral" : pct >= 95 ? "good" : pct >= 70 ? "ok" : "bad";
              // SVG attrs accept var(--*) in modern browsers; fall back to a CSS variable
              // so theme changes (light/dark) propagate without a JS re-render.
              const toneColor = tone === "good" ? "var(--good)" : tone === "ok" ? "var(--warn-mid)" : tone === "bad" ? "var(--warn)" : "var(--ink-3)";
              const showLabel = realH > 22 || (p.real != null && p.real > 0);
              return (
                <g key={`${r.key}-${s.ae.id}`}>
                  {/* Target outline (always shown if there's a target) */}
                  {targetH > 0 && (
                    <rect x={x} y={yTarget} width={barW} height={targetH}
                      fill="none" stroke={s.color} strokeWidth="1" strokeDasharray="2 2" opacity="0.45" rx="2" />
                  )}
                  {/* Real bar — colored by AE, opacity by tone */}
                  {realH > 0 && (
                    <g>
                      <rect x={x} y={yReal} width={barW} height={realH}
                        fill={s.color} rx="2"
                        opacity={tone === "good" ? 1 : tone === "ok" ? 0.85 : tone === "bad" ? 0.65 : 0.85} />
                      {/* Tone stripe at top of bar */}
                      <rect x={x} y={yReal} width={barW} height={3} fill={toneColor} rx="1.5" />
                    </g>
                  )}
                  {/* % achieved label — above the taller of the two bars */}
                  {showLabel && pct != null && (() => {
                    const labelY = Math.min(yReal, yTarget) - 6;
                    const labelText = `${Math.round(pct)}%`;
                    const lw = labelText.length * 7 + 8;
                    return (
                      <g>
                        <rect x={cx - lw/2} y={labelY - 12} width={lw} height={15} rx={7.5}
                          fill="white" stroke={toneColor} strokeWidth="1" />
                        <text x={cx} y={labelY} textAnchor="middle" fontSize={window.TOKENS.fs.caption} fontWeight="700"
                          fill={toneColor} fontFamily="Geist Mono, monospace">{labelText}</text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* X-axis labels: month names */}
      {rows.map((r, i) => (
        <text key={r.key} x={xMonthCenter(i)} y={pad.t + innerH + 28} textAnchor="middle"
          className={`chart__xlabel ${r.key === todayMonthKey ? "chart__xlabel--today" : ""}`}>
          {window.MONTH_SHORT[r.key]}
        </text>
      ))}
    </svg>

    {/* Legend below chart — centered chips, name + slot.
        Phase 3f — swatch routes through the v2 <Swatch> primitive.
        AE chart series colors come from the sequential chart palette
        (TOKENS.chart[0..n] cycled by series index), not a tokenized
        tone — pass via the explicit `color` escape hatch on Swatch. */}
    <div className="ae-legend">
      {series.map((s, idx) => {
        const personName = s.ae.person || "";
        const slotName = s.ae.name || `AE ${idx+1}`;
        const primary = personName || slotName;
        const showSlot = personName && personName !== slotName;
        return (
          <div key={s.ae.id} className="ae-legend__chip">
            <V2Swatch color={s.color} size={10} />
            <span className="ae-legend__name">{primary}</span>
            {showSlot && <span className="ae-legend__slot">{slotName}</span>}
          </div>
        );
      })}
    </div>
    </div>
  );
}

export default AEComparisonChart;
